import { useCallback, useMemo, useState, useEffect } from 'react';
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import RsaNode from './RsaNode';
import FornitoreNode from './FornitoreNode';
import TipologiaNode from './TipologiaNode';

const nodeTypes = {
  rsa: RsaNode,
  fornitore: FornitoreNode,
  tipologia: TipologiaNode
};

const defaultEdgeOptions = {
  animated: false,
  style: { stroke: '#94a3b8', strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#94a3b8'
  }
};

function NetworkGraph({
  initialNodes = [],
  initialEdges = [],
  onNodeClick,
  onNodeDoubleClick
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Sync nodes and edges when props change
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Calcola nodi e edge con highlight
  const highlightedData = useMemo(() => {
    if (!selectedNodeId) {
      return {
        nodes: nodes.map(n => ({
          ...n,
          style: { ...n.style, opacity: 1 }
        })),
        edges: edges.map(e => ({
          ...e,
          style: { ...defaultEdgeOptions.style, opacity: 1 }
        }))
      };
    }

    // Trova nodi connessi
    const connectedIds = new Set([selectedNodeId]);
    edges.forEach(e => {
      if (e.source === selectedNodeId) connectedIds.add(e.target);
      if (e.target === selectedNodeId) connectedIds.add(e.source);
    });

    return {
      nodes: nodes.map(n => ({
        ...n,
        style: {
          ...n.style,
          opacity: connectedIds.has(n.id) ? 1 : 0.25,
          transition: 'opacity 0.3s ease'
        }
      })),
      edges: edges.map(e => ({
        ...e,
        style: {
          ...defaultEdgeOptions.style,
          opacity: (e.source === selectedNodeId || e.target === selectedNodeId) ? 1 : 0.15,
          stroke: (e.source === selectedNodeId || e.target === selectedNodeId) ? '#3b82f6' : '#94a3b8',
          strokeWidth: (e.source === selectedNodeId || e.target === selectedNodeId) ? 3 : 2,
          transition: 'all 0.3s ease'
        },
        animated: e.source === selectedNodeId || e.target === selectedNodeId
      }))
    };
  }, [nodes, edges, selectedNodeId]);

  const handleNodeClick = useCallback((event, node) => {
    setSelectedNodeId(prev => (prev === node.id ? null : node.id));
    if (onNodeClick) onNodeClick(node);
  }, [onNodeClick]);

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const handleNodeDoubleClick = useCallback((event, node) => {
    if (onNodeDoubleClick) onNodeDoubleClick(node);
  }, [onNodeDoubleClick]);

  return (
    <div className="w-full h-full">
      <ReactFlow
        nodes={highlightedData.nodes}
        edges={highlightedData.edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        attributionPosition="bottom-left"
      >
        <Controls position="top-right" />
        <MiniMap
          position="bottom-right"
          nodeColor={(node) => {
            if (node.type === 'rsa') return '#3b82f6';
            if (node.type === 'fornitore') return '#22c55e';
            if (node.type === 'tipologia') return '#a855f7';
            return '#94a3b8';
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
          className="!bg-white !border !border-gray-200 !rounded-lg"
        />
        <Background variant="dots" gap={20} size={1} color="#e5e7eb" />
      </ReactFlow>
    </div>
  );
}

export default NetworkGraph;
